import React, { useState, useEffect } from "react";
import { FaArrowRight, FaCog } from "react-icons/fa";

const navLinks = [
  { name: "Home", id: "home" },
  { name: "About", id: "about" },
  { name: "Motorcycles", id: "motorcycles" },
  { name: "Contact", id: "contact" },
];

const Navbar = ({ onOpenModal }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <style>{`
        /* ── FONT CHANGE: Oswald (display) + DM Sans (body) ── */
        @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=DM+Sans:wght@400;500;600&family=Barlow+Condensed:wght@400;600;700;800&display=swap');

        .font-bebas       { font-family: 'Oswald', sans-serif; letter-spacing: 0.02em; }
        .font-barlow      { font-family: 'DM Sans', sans-serif; }
        .font-barlow-cond { font-family: 'Barlow Condensed', sans-serif; }

        .nav-link::after {
          content: '';
          position: absolute;
          bottom: -4px; left: 0;
          width: 0; height: 1px;
          background: #D4AF37;
          transition: width 0.3s ease;
        }
        .nav-link:hover::after { width: 100%; }
        .nav-link:hover { color: #F5F5F5 !important; }

        .logo-hex {
          clip-path: polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%);
          animation: hexGlow 3s ease-in-out infinite;
        }
        @keyframes hexGlow {
          0%,100% { box-shadow: 0 0 8px rgba(212,175,55,0.4); }
          50% { box-shadow: 0 0 20px rgba(212,175,55,0.8), 0 0 40px rgba(212,175,55,0.3); }
        }

        .btn-slash {
          clip-path: polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%);
        }

        .ph-dot {
          animation: pulseDot 1.5s infinite;
        }
        @keyframes pulseDot {
          0%,100% { opacity:1; transform:scale(1); }
          50% { opacity:0.4; transform:scale(0.7); }
        }
      `}</style>

      <nav
        className={`fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 md:px-12 py-4 transition-all duration-500 ${
          scrolled
            ? "bg-[#050505]/95 backdrop-blur-md border-b border-[#D4AF37]/10"
            : "bg-gradient-to-b from-[#050505]/90 to-transparent"
        }`}
      >
        {/* LOGO */}
        <div className="flex items-center gap-3">
          <div className="logo-hex w-10 h-10 bg-[#D4AF37] flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
            <FaCog />
          </div>
          <div className="flex flex-col leading-none">
            {/* Logo name — Oswald */}
            <span className="font-bebas text-[22px] tracking-widest text-[#F5F5F5]">
              Sri Kalikambal
            </span>
            <span className="font-barlow-cond text-[10px] tracking-[4px] pt-2 text-[#D4AF37] uppercase">
              Autos · Est. 2005
            </span>
          </div>
        </div>

        {/* Desktop nav links */}
        <ul className="hidden lg:flex items-center gap-10">
          {navLinks.map((item) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                className="font-barlow-cond text-[13px] tracking-[3px] uppercase text-[#9CA3AF] cursor-pointer relative nav-link transition-colors duration-300"
              >
                {item.name}
              </a>
            </li>
          ))}
        </ul>

        {/* RIGHT */}
        <div className="hidden md:flex items-center gap-4">
          {/* Phone */}
          <div className="flex items-center gap-2 border border-[#D4AF37]/20 px-4 py-2 hover:border-[#D4AF37]/60 transition-colors duration-300">
            <span className="ph-dot w-2 h-2 rounded-full bg-[#D4AF37] flex-shrink-0"></span>
            {/* Phone number — DM Sans, brighter */}
            <span className="font-barlow text-[14px] font-medium text-[#9CA3AF] tracking-wide whitespace-nowrap hover:text-[#F5F5F5] transition-colors">
              +91 88797 37412
            </span>
          </div>

          {/* CTA */}
          <button onClick={onOpenModal} className="btn-slash flex items-center gap-3 bg-[#D4AF37] text-white pl-6 pr-4 py-[10px] font-barlow-cond text-[13px] font-bold tracking-[2px] uppercase hover:bg-[#E5C158] transition-colors duration-300">
            Book Test Ride
            <div className="w-8 h-8 bg-black/20 flex items-center justify-center text-sm">
              <FaArrowRight />
            </div>
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          className="lg:hidden flex flex-col gap-[5px] p-2"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span className={`block w-6 h-[2px] bg-[#D4AF37] transition-all duration-300 ${menuOpen ? "rotate-45 translate-y-[7px]" : ""}`}></span>
          <span className={`block w-6 h-[2px] bg-[#F5F5F5] transition-all duration-300 ${menuOpen ? "opacity-0" : ""}`}></span>
          <span className={`block w-6 h-[2px] bg-[#D4AF37] transition-all duration-300 ${menuOpen ? "-rotate-45 -translate-y-[7px]" : ""}`}></span>
        </button>
      </nav>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-40 bg-[#050505]/98 flex flex-col items-center justify-center gap-8 font-bebas">
          {navLinks.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              className="text-5xl tracking-widest text-[#F5F5F5] hover:text-[#D4AF37] transition-colors duration-300"
              onClick={() => setMenuOpen(false)}
            >
              {item.name}
            </a>
          ))}
          <div className="mt-6 flex flex-col items-center gap-4">
            {/* Mobile phone — DM Sans, brighter */}
            <span className="font-barlow text-[#9CA3AF] text-[15px]">+91 88797 37412</span>
            <button onClick={onOpenModal} className="btn-slash bg-[#D4AF37] text-white px-10 py-3 font-barlow-cond text-sm tracking-[3px] uppercase">
              Book Test Ride
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;